import { createScrubber, scrollProgress } from './fm-scrub.js';

// Same timing as the original Vue homepage: first 0–40%, switch at 50%,
// second 50–90%. Both clips occupy one sticky stage, before the shop.
export function sequenceState(p) {
  const progress = Math.min(1, Math.max(0, p));
  // Blend the held end/start frames within the existing inter-film pause.
  // Media readyState must never decide which film is visible during seeking.
  const blend = Math.min(1, Math.max(0, (progress - .44) / .06));
  return {
    active: progress < .5 ? 0 : 1,
    secondOpacity: blend * blend * (3 - 2 * blend),
    ratios: [Math.min(1, progress / .4), Math.max(0, Math.min(1, (progress - .5) / .4))],
    copyOpacity: Math.max(0, 1 - progress * 8),
    preloadSecond: progress >= .2,
  };
}

if (!customElements.get('fm-film-sequence')) {
  customElements.define('fm-film-sequence', class extends HTMLElement {
    connectedCallback() {
      this.dispose?.();
      const cleanup = new AbortController(), { signal } = cleanup;
      const videos = [...this.querySelectorAll('video')];
      const stage = this.querySelector('.fm-sequence-stage');
      const copy = this.querySelector('.fm-sequence-copy');
      const label = this.querySelector('.fm-sequence-label');
      const toggle = this.querySelector('.fm-sequence-toggle');
      const status = this.querySelector('.fm-sequence-status');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const scrubbers = videos.map(video => createScrubber(video));
      const loaded = [false, false];
      let manual = reduced.matches, frame = null, state = sequenceState(0);
      const load = i => {
        if (loaded[i]) return;
        loaded[i] = true;
        videos[i].preload = 'auto';
        videos[i].load();
      };
      const update = () => {
        frame = null;
        const rect = this.getBoundingClientRect();
        const top = parseFloat(getComputedStyle(stage).top) || 0;
        const visible = rect.bottom > top && rect.top < innerHeight && !document.hidden;
        const p = scrollProgress(rect.top, this.offsetHeight, stage.clientHeight, top);
        state = sequenceState(p);
        if (visible) load(0);
        if (manual || (visible && state.preloadSecond)) load(1);
        // Seeking can temporarily lower readyState. Never reveal the first
        // film again because the second is buffering: retain its layer/poster.
        this.style.setProperty('--fm-second-opacity', String(state.secondOpacity));
        this.style.setProperty('--fm-progress', String(p));
        this.style.setProperty('--fm-copy-opacity', String(state.copyOpacity));
        copy.inert = !manual && state.copyOpacity === 0;
        label.textContent = manual ? 'Silver / Black' : state.active === 0 ? '01 / Silver' : '02 / Black';
        const current = videos[state.active];
        status.textContent = manual ? '' : current.error
          ? 'Film unavailable. Try the manual video controls.'
          : current.readyState < 2 ? 'Loading film… You can keep scrolling.' : '';
        scrubbers.forEach((scrubber, i) => {
          // Prepare the hidden first frame before the handoff, and keep the
          // first film's final frame correct even after a large scroll jump.
          scrubber.enable(!manual && visible && loaded[i]);
          scrubber.set(state.ratios[i]);
        });
      };
      const queue = () => { if (frame === null) frame = requestAnimationFrame(update); };
      const setMode = () => {
        this.classList.toggle('is-scroll', !manual);
        videos.forEach(v => { v.pause(); v.controls = manual; });
        toggle.textContent = manual ? 'Use scroll animation' : 'Play videos manually';
        toggle.hidden = reduced.matches;
        if (manual) { load(0); load(1); }
        queue();
      };
      toggle.addEventListener('click', () => { manual = !manual; setMode(); }, { signal });
      // Real gestures unlock mobile decoding without calling play() every scroll.
      const prime = () => { if (!manual) { load(state.active); scrubbers[state.active].prime(); } };
      this.addEventListener('pointerdown', prime, { passive: true, signal });
      this.addEventListener('touchstart', prime, { passive: true, signal });
      document.addEventListener('scroll', queue, { capture: true, passive: true, signal });
      window.addEventListener('resize', queue, { passive: true, signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) videos.forEach(v => v.pause());
        queue();
      }, { signal });
      reduced.addEventListener('change', () => { manual = reduced.matches; setMode(); }, { signal });
      videos.forEach(v => ['loadedmetadata', 'loadeddata', 'canplay', 'seeked', 'error'].forEach(
        event => v.addEventListener(event, queue, { signal }),
      ));
      this.dispose = () => {
        cleanup.abort();
        if (frame !== null) cancelAnimationFrame(frame);
        scrubbers.forEach(s => s.destroy());
        videos.forEach(v => v.pause());
      };
      setMode();
    }
    disconnectedCallback() { this.dispose?.(); }
  });
}
